import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ROUTE_PATHS } from "@/lib/index";
import { ALLOWED_EMAILS } from "@/App";
import { springPresets } from "@/lib/motion";

function validateEmail(e: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}
function validatePassword(p: string) {
  return p.length >= 8;
}

function AuthInput({
  icon: Icon, type, placeholder, value, onChange, error, toggle, onToggle,
}: {
  icon: React.ElementType; type: string; placeholder: string;
  value: string; onChange: (v: string) => void;
  error?: string; toggle?: boolean; onToggle?: () => void;
}) {
  return (
    <div className="space-y-1">
      <div className="relative">
        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
          style={{ color: error ? "rgba(220,80,80,0.7)" : "rgba(140,155,175,0.5)" }} />
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full h-12 pl-11 pr-11 text-sm rounded-xl outline-none transition-all duration-200"
          style={{
            background: "oklch(0.10 0.022 220)",
            border: `1px solid ${error ? "rgba(220,80,80,0.4)" : "oklch(0.20 0.03 220)"}`,
            color: "rgba(210,225,240,0.9)",
            boxShadow: error ? "0 0 0 3px rgba(220,80,80,0.08)" : "none",
          }}
          onFocus={e => {
            e.currentTarget.style.border = `1px solid ${error ? "rgba(220,80,80,0.6)" : "rgba(212,175,55,0.4)"}`;
            e.currentTarget.style.boxShadow = error
              ? "0 0 0 3px rgba(220,80,80,0.1)"
              : "0 0 0 3px rgba(212,175,55,0.08)";
          }}
          onBlur={e => {
            e.currentTarget.style.border = `1px solid ${error ? "rgba(220,80,80,0.4)" : "oklch(0.20 0.03 220)"}`;
            e.currentTarget.style.boxShadow = error ? "0 0 0 3px rgba(220,80,80,0.08)" : "none";
          }}
        />
        {toggle && (
          <button type="button" onClick={onToggle}
            className="absolute right-4 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-80"
            style={{ color: "rgba(140,155,175,0.5)" }}>
            {type === "password" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
      {error && (
        <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
          className="text-xs px-1" style={{ color: "rgba(220,100,100,0.85)" }}>
          {error}
        </motion.p>
      )}
    </div>
  );
}

function RegisterForm() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!firstName.trim()) e.firstName = "Imię jest wymagane";
    if (!lastName.trim()) e.lastName = "Nazwisko jest wymagane";
    if (!validateEmail(email)) e.email = "Podaj poprawny adres e-mail";
    if (!validatePassword(password)) e.password = "Hasło musi mieć co najmniej 8 znaków";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;

    const normalizedEmail = email.trim().toLowerCase();
    if (!ALLOWED_EMAILS.includes(normalizedEmail)) {
      setErrors({ general: "Rejestracja jest aktualnie zamknięta. Skontaktuj się z administratorem." });
      return;
    }

    setLoading(true);
    setErrors({});

    // Adres callback — musi być dodany do Supabase Dashboard > Auth > URL Configuration > Redirect URLs
    const redirectTo = `${window.location.origin}/auth/callback`;

    const { error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        emailRedirectTo: redirectTo,
        data: { first_name: firstName.trim(), last_name: lastName.trim() },
      },
    });

    setLoading(false);
    if (error) {
      if (error.message.includes("already registered")) {
        setErrors({ email: "Ten adres e-mail jest już zarejestrowany" });
      } else {
        setErrors({ general: error.message });
      }
    } else {
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="text-center py-6 space-y-4">
        <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
          style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.25)" }}>
          <Mail className="w-6 h-6" style={{ color: "rgba(212,175,55,0.8)" }} />
        </div>
        <h3 className="text-xl font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(230,220,200,0.95)" }}>
          Sprawdź swoją skrzynkę
        </h3>
        <p className="text-sm leading-relaxed" style={{ color: "rgba(155,170,190,0.75)" }}>
          Wysłaliśmy link aktywacyjny na adres<br />
          <span style={{ color: "rgba(212,175,55,0.8)" }}>{email}</span>.<br />
          Kliknij go, aby aktywować konto.
        </p>
        <Link to={ROUTE_PATHS.LOGOWANIE}
          className="inline-block mt-2 text-sm transition-opacity hover:opacity-80"
          style={{ color: "rgba(212,175,55,0.7)" }}>
          Przejdź do logowania →
        </Link>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
              style={{ color: errors.firstName ? "rgba(220,80,80,0.7)" : "rgba(140,155,175,0.5)" }} />
            <input
              type="text" placeholder="Imię" value={firstName}
              onChange={e => setFirstName(e.target.value)}
              className="w-full h-12 pl-11 pr-4 text-sm rounded-xl outline-none transition-all duration-200"
              style={{ background: "oklch(0.10 0.022 220)", border: `1px solid ${errors.firstName ? "rgba(220,80,80,0.4)" : "oklch(0.20 0.03 220)"}`, color: "rgba(210,225,240,0.9)" }}
            />
          </div>
          {errors.firstName && <p className="text-xs px-1" style={{ color: "rgba(220,100,100,0.85)" }}>{errors.firstName}</p>}
        </div>
        <div className="space-y-1">
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
              style={{ color: errors.lastName ? "rgba(220,80,80,0.7)" : "rgba(140,155,175,0.5)" }} />
            <input
              type="text" placeholder="Nazwisko" value={lastName}
              onChange={e => setLastName(e.target.value)}
              className="w-full h-12 pl-11 pr-4 text-sm rounded-xl outline-none transition-all duration-200"
              style={{ background: "oklch(0.10 0.022 220)", border: `1px solid ${errors.lastName ? "rgba(220,80,80,0.4)" : "oklch(0.20 0.03 220)"}`, color: "rgba(210,225,240,0.9)" }}
            />
          </div>
          {errors.lastName && <p className="text-xs px-1" style={{ color: "rgba(220,100,100,0.85)" }}>{errors.lastName}</p>}
        </div>
      </div>

      <AuthInput icon={Mail} type="email" placeholder="Adres e-mail"
        value={email} onChange={setEmail} error={errors.email} />

      <AuthInput icon={Lock} type={showPass ? "text" : "password"} placeholder="Hasło (min. 8 znaków)"
        value={password} onChange={setPassword} error={errors.password}
        toggle onToggle={() => setShowPass(p => !p)} />

      {errors.general && (
        <p className="text-xs px-1 text-center" style={{ color: "rgba(220,100,100,0.85)" }}>{errors.general}</p>
      )}

      <button type="submit" disabled={loading}
        className="w-full h-12 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all duration-200 hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ background: "rgba(212,175,55,0.88)", color: "rgba(6,8,18,0.95)" }}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Utwórz konto <ArrowRight className="w-4 h-4" /></>}
      </button>

      <p className="text-center text-xs leading-relaxed" style={{ color: "rgba(120,135,155,0.6)" }}>
        Rejestrując się, akceptujesz naszą{" "}
        <Link to={ROUTE_PATHS.POLITYKA}
          target="_blank"
          className="underline underline-offset-2 transition-opacity hover:opacity-80"
          style={{ color: "rgba(212,175,55,0.6)" }}>
          politykę prywatności
        </Link>
        {" "}i{" "}
        <Link to={ROUTE_PATHS.REGULAMIN}
          target="_blank"
          className="underline underline-offset-2 transition-opacity hover:opacity-80"
          style={{ color: "rgba(212,175,55,0.6)" }}>
          regulamin
        </Link>
        .
      </p>
    </form>
  );
}

function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from || ROUTE_PATHS.KONTO;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const e: Record<string, string> = {};
    if (!validateEmail(email)) e.email = "Podaj poprawny adres e-mail";
    if (!password) e.password = "Hasło jest wymagane";
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setLoading(true);
    setErrors({});

    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    setLoading(false);
    if (error) {
      if (error.message.includes("Invalid login")) {
        setErrors({ general: "Nieprawidłowy e-mail lub hasło" });
      } else if (error.message.includes("Email not confirmed")) {
        setErrors({ general: "Potwierdź adres e-mail przed zalogowaniem" });
      } else {
        setErrors({ general: error.message });
      }
    } else {
      navigate(from, { replace: true });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <AuthInput icon={Mail} type="email" placeholder="Adres e-mail"
        value={email} onChange={setEmail} error={errors.email} />

      <AuthInput icon={Lock} type={showPass ? "text" : "password"} placeholder="Hasło"
        value={password} onChange={setPassword} error={errors.password}
        toggle onToggle={() => setShowPass(p => !p)} />

      {errors.general && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="text-xs text-center px-1 py-2 rounded-lg"
          style={{ color: "rgba(220,100,100,0.9)", background: "rgba(220,80,80,0.08)", border: "1px solid rgba(220,80,80,0.15)" }}>
          {errors.general}
        </motion.p>
      )}

      <button type="submit" disabled={loading}
        className="w-full h-12 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all duration-200 hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ background: "rgba(212,175,55,0.88)", color: "rgba(6,8,18,0.95)" }}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Zaloguj się <ArrowRight className="w-4 h-4" /></>}
      </button>

      <div className="text-center">
        <button type="button" className="text-xs transition-opacity hover:opacity-80"
          style={{ color: "rgba(140,155,175,0.6)" }}
          onClick={async () => {
            if (!validateEmail(email)) { setErrors({ email: "Podaj e-mail, aby zresetować hasło" }); return; }
            await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase());
            setErrors({ general: "Link do resetu hasła został wysłany na Twój e-mail." });
          }}>
          Zapomniałem hasła
        </button>
      </div>
    </form>
  );
}

export default function AuthPage() {
  const location = useLocation();
  const isLogin = location.pathname === ROUTE_PATHS.LOGOWANIE;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16"
      style={{ background: "linear-gradient(135deg, oklch(0.07 0.015 235) 0%, oklch(0.10 0.025 220) 100%)" }}>

      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-5"
          style={{ background: "radial-gradient(circle, rgba(212,175,55,0.8), transparent)", filter: "blur(80px)" }} />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full opacity-5"
          style={{ background: "radial-gradient(circle, oklch(0.65 0.15 220), transparent)", filter: "blur(80px)" }} />
      </div>

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        transition={springPresets.gentle} className="w-full max-w-md relative">

        <div className="text-center mb-8">
          <Link to={ROUTE_PATHS.HOME}>
            <span className="text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(212,175,55,0.75)" }}>
              e-grób
            </span>
          </Link>
          <div className="w-12 h-px mx-auto mt-3"
            style={{ background: "linear-gradient(to right, transparent, rgba(212,175,55,0.35), transparent)" }} />
        </div>

        <div className="rounded-2xl overflow-hidden"
          style={{ background: "oklch(0.09 0.018 225)", border: "1px solid oklch(0.20 0.035 220)", boxShadow: "0 32px 80px rgba(0,0,0,0.6)" }}>

          <div className="flex border-b" style={{ borderColor: "oklch(0.15 0.025 220)" }}>
            {[
              { label: "Zaloguj się", path: ROUTE_PATHS.LOGOWANIE },
              { label: "Utwórz konto", path: ROUTE_PATHS.REJESTRACJA },
            ].map(tab => {
              const isActive = location.pathname === tab.path;
              return (
                <Link key={tab.path} to={tab.path}
                  className="flex-1 py-4 text-center text-sm font-medium transition-all duration-200 relative"
                  style={{ color: isActive ? "rgba(212,175,55,0.85)" : "rgba(130,145,165,0.6)" }}>
                  {tab.label}
                  {isActive && (
                    <motion.div layoutId="auth-tab-indicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5"
                      style={{ background: "rgba(212,175,55,0.6)" }} />
                  )}
                </Link>
              );
            })}
          </div>

          <div className="px-7 py-7">
            <AnimatePresence mode="wait">
              <motion.div key={isLogin ? "login" : "register"}
                initial={{ opacity: 0, x: isLogin ? -12 : 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: isLogin ? 12 : -12 }}
                transition={{ duration: 0.2 }}>
                {isLogin ? <LoginForm /> : <RegisterForm />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to={ROUTE_PATHS.HOME} className="text-xs transition-opacity hover:opacity-70"
            style={{ color: "rgba(120,135,155,0.55)" }}>
            ← Wróć na stronę główną
          </Link>
        </div>
      </motion.div>
    </div>
  );
}